/**
 * ProjectionChart Component
 *
 * Displays the YoY spot price projection for the selected barra.
 * Shows monthly prices over time with yearly averages.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import { TrendingUp, TrendingDown, Info, ChevronDown, ChevronUp, BarChart3 } from 'lucide-react';
import { clsx } from 'clsx';
import { projectionService } from '../../../services/api/projection.service';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface ProjectionChartProps {
  barra: string;
  className?: string;
}

type ViewMode = 'monthly' | 'yearly';

export function ProjectionChart({ barra, className }: ProjectionChartProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('yearly');

  // Fetch projection data for the selected barra
  const { data, isLoading, error } = useQuery({
    queryKey: ['barra-projection', barra],
    queryFn: () => projectionService.getBarraProjection(barra),
    enabled: !!barra,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });

  if (!barra) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={clsx('bg-white border border-swiss-mist rounded-lg p-4', className)}>
        <div className="flex items-center gap-2 animate-pulse">
          <div className="w-8 h-8 bg-gray-200 rounded-lg" />
          <div className="h-4 bg-gray-200 rounded w-48" />
        </div>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className={clsx('bg-red-50 border border-red-200 rounded-lg p-4', className)}>
        <p className="text-sm text-red-600">
          {t('projection.chartError', 'Error loading projection data')}
        </p>
      </div>
    );
  }

  const { monthlyData, yearlyAverages, statistics, years } = data;

  // Prepare chart data based on view mode
  const getChartData = () => {
    if (viewMode === 'yearly') {
      // Yearly averages view
      const labels = years.map(y => y.toString());
      const prices = years.map(y => yearlyAverages[y] || 0);

      return {
        labels,
        datasets: [
          {
            label: t('projection.yearlyAvg', 'Promedio Anual (USD/MWh)'),
            data: prices,
            borderColor: 'rgb(147, 51, 234)',
            backgroundColor: 'rgba(147, 51, 234, 0.1)',
            tension: 0.3,
            fill: true,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'rgb(147, 51, 234)',
          },
        ],
      };
    } else {
      // Monthly view - show all months
      const labels = monthlyData.map(d => `${d.monthName} ${d.year}`);
      const prices = monthlyData.map(d => d.price);

      return {
        labels,
        datasets: [
          {
            label: t('projection.monthlyPrice', 'Precio Mensual (USD/MWh)'),
            data: prices,
            borderColor: 'rgb(147, 51, 234)',
            backgroundColor: 'rgba(147, 51, 234, 0.05)',
            tension: 0.2,
            fill: true,
            pointRadius: 0,
            pointHoverRadius: 4,
          },
        ],
      };
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        callbacks: {
          label: (context: any) => {
            const value = context.raw;
            return `${value.toFixed(2)} USD/MWh`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: { size: 10 },
          autoSkip: true,
          maxTicksLimit: viewMode === 'yearly' ? 25 : 12,
        },
      },
      y: {
        title: {
          display: true,
          text: 'USD/MWh',
          font: { size: 11 },
        },
        ticks: {
          font: { size: 10 },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
    },
  };

  const trendIsPositive = (statistics.trendPercent || 0) > 0;
  const TrendIcon = trendIsPositive ? TrendingUp : TrendingDown;

  return (
    <div className={clsx('bg-white border border-purple-200 rounded-lg overflow-hidden', className)}>
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-white cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-purple-600" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-swiss-charcoal">
              {t('projection.chartTitle', 'Proyeccion de Precios Spot')}
            </h4>
            <p className="text-xs text-swiss-stone">
              {barra} • {statistics.startYear} - {statistics.endYear}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Trend indicator */}
          <div className={clsx(
            'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium',
            trendIsPositive ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
          )}>
            <TrendIcon className="w-3 h-3" />
            {(statistics.trendPercent || 0) > 0 ? '+' : ''}{(statistics.trendPercent || 0).toFixed(1)}%
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-swiss-stone" />
          ) : (
            <ChevronDown className="w-5 h-5 text-swiss-stone" />
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* View mode toggle */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('yearly')}
                className={clsx(
                  'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                  viewMode === 'yearly'
                    ? 'bg-purple-600 text-white'
                    : 'bg-swiss-pearl text-swiss-stone hover:bg-swiss-mist'
                )}
              >
                {t('projection.viewYearly', 'Anual')}
              </button>
              <button
                onClick={() => setViewMode('monthly')}
                className={clsx(
                  'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                  viewMode === 'monthly'
                    ? 'bg-purple-600 text-white'
                    : 'bg-swiss-pearl text-swiss-stone hover:bg-swiss-mist'
                )}
              >
                {t('projection.viewMonthly', 'Mensual')}
              </button>
            </div>
          </div>

          {/* Chart */}
          <div style={{ height: '250px' }}>
            <Line data={getChartData()} options={chartOptions} />
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-4 gap-3">
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-xs text-swiss-stone">{t('projection.avgPrice', 'Promedio')}</p>
              <p className="text-sm font-semibold text-purple-700">
                ${statistics.avgPrice.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-xs text-swiss-stone">{t('projection.minPrice', 'Minimo')}</p>
              <p className="text-sm font-semibold text-green-700">
                ${statistics.minPrice.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-xs text-swiss-stone">{t('projection.maxPrice', 'Maximo')}</p>
              <p className="text-sm font-semibold text-red-700">
                ${statistics.maxPrice.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-xs text-swiss-stone">{t('projection.totalYears', 'Anos')}</p>
              <p className="text-sm font-semibold text-purple-700">
                {years.length}
              </p>
            </div>
          </div>

          {/* Info note */}
          <div className="flex items-start gap-2 p-3 bg-swiss-pearl rounded-lg">
            <Info className="w-4 h-4 text-swiss-stone flex-shrink-0 mt-0.5" />
            <p className="text-xs text-swiss-graphite">
              {t(
                'projection.chartInfo',
                'Esta proyeccion muestra la evolucion esperada de los precios spot para el nodo seleccionado. Los valores se utilizan para calcular ahorros variables ano a ano en el analisis financiero.'
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
